/**
 * MicSilenceWatch — shared "is the microphone actually hearing anything?" watchdog.
 *
 * Born from voice-cover job #26513 (2026-07-16): a Premium user burned 45 minutes on
 * four liveness refusals because her mic recorded 13 s of digital silence (-91 dB) —
 * the passive level LED never told her "we can't hear you". Dictaphone has carried
 * its own inline version of this for months and its telemetry proves the pattern:
 * 396 sessions/30 d hit the warning (top state: track live.running — mic alive, no
 * signal) and those sessions then massively reach tool_complete. This module lifts
 * that proven mechanism into a shared primitive so every RECORDING tool can attach
 * it in three lines. (Dictaphone keeps its own copy — it adds an auto-fix that
 * re-requests the mic without echoCancellation:false, which is specific to it.)
 *
 * WHERE IT BELONGS: tools that record VOICE/SPEECH (voice-cover, voice-clone,
 * record, voice-translator, voice-changer, …) — silence there means a broken input.
 * WHERE IT MUST NOT GO: tools that MEASURE with the mic — guitar-tuner, sound-meter,
 * hearing-test, instrument-identifier, and especially snoring-monitor, whose whole
 * job is recording silence. A silence warning there is a false alarm by design.
 *
 * Usage:
 *   var watch = MicSilenceWatch.attach({
 *     stream:    mediaStream,        // OR analyser: an existing AnalyserNode
 *     slug:      'voice-cover',      // for the shared mic_silence_warned telemetry
 *     silenceMs: 4000,               // continuous silence before onSilence fires
 *     onSilence: function(info){ ... }, // show YOUR inline warning (once per episode);
 *                                    // info = {alive, cause: 'silent'|'muted'|'ended'}
 *     onSignal:  function(){ ... },  // hide it (signal came back)
 *   });
 *   ...
 *   watch.stop();                    // on recorder stop; watch.maxPeak() for a
 *                                    // post-take "was this whole take silent?" check
 *
 * No UI inside — the tool owns its warning's look and copy. The ~500 ms cadence is
 * dictaphone's production-proven value; the decision is made on SMOOTHED FREQUENCY
 * data (see tick() — raw time-domain samples judge the mic through an 8 % keyhole
 * and accuse working microphones); 4 s default fits short takes (dictaphone itself uses 15 s — its comment
 * warns the old 5 s caused rage-quit false alarms on LONG recordings, but a voice
 * reference is read aloud from its first second, so early silence IS the signal).
 *
 * The window counts only while the input has NEVER carried sound in this watch
 * (see tick(): once it has spoken, silence is a pause, and only facts — the source
 * ended the track, the system muted it — still warn).
 */
(function () {
    'use strict';

    function attach(opts) {
        opts = opts || {};
        var silenceMs = opts.silenceMs || 4000;
        var checkMs = opts.checkMs || 500;
        var analyser = opts.analyser || null;
        var ctx = null;
        var stopped = false;
        var warned = false;
        var silentMs = 0;
        var maxPeak = 0;

        if (!analyser && opts.stream) {
            try {
                var AC = window.AudioContext || window.webkitAudioContext;
                ctx = new AC();
                analyser = ctx.createAnalyser();
                analyser.fftSize = 2048;
                /* Cloning the track to get an independent consumer was tried
                   and measured WORSE — /reverse, which read the shared stream
                   correctly, went silent on a clone. Read the stream itself. */
                ctx.createMediaStreamSource(opts.stream).connect(analyser);
            } catch (_) { analyser = null; }
        }
        if (!analyser) return { stop: function () {}, maxPeak: function () { return -1; } };

        /* A measurement taken on a stopped clock is not evidence about the
           microphone. attach() runs after the getUserMedia await, so the
           gesture is spent and the context we just built can start — and stay
           — suspended; its analyser then returns digital silence forever and
           this watchdog tells the user their microphone is broken when it is
           our own clock that stopped. Reproduced on /voice-changer with a
           beeping mic: "🔇 Мы вас не слышим" while the input was fine.
           So: while the clock is stopped we try to start it and skip the tick.
           (mic-manager also binds this context to its gesture-healer, which is
           what rescues the case where resume() outside a gesture is refused.) */
        function clockRunning() {
            if (!ctx) return true;                 // caller-supplied analyser — their clock, their business
            if (ctx.state === 'running') return true;
            try {
                if (window.__safeResume) window.__safeResume(ctx);
                else ctx.resume().catch(function () {});
            } catch (_) {}
            return false;
        }

        /* A track the PAGE stopped is the end of a recording, not silence.
           Tools stop their tracks when the take ends and not every one of them
           stops this watch too, so it kept sampling a dead stream and, 15 s
           later, told the person «we can't hear you» about a recording they
           had already finished. Measured 2026-09-24, 30 days of
           mic_silence_warned: /reverse — 3768 events in 1085 sessions, EVERY
           one of them on an ended track; /voice-changer 85 sessions,
           /recording-studio 25, /speech-to-text 14, /screen-share 8.
           The spec separates the two ways a track ends: the `ended` event
           fires only when the SOURCE ends it (device unplugged, permission
           revoked), never on the page's own stop(). So a track that ended
           without the event is ours to ignore; one the source ended still
           warns — the change-microphone button is the right offer there. */
        var sourceEnded = false;
        function audioTracks() {
            try { return (opts.stream && opts.stream.getAudioTracks) ? opts.stream.getAudioTracks() : []; } catch (_) { return []; }
        }
        audioTracks().forEach(function (t) {
            try { t.addEventListener('ended', function () { sourceEnded = true; }); } catch (_) {}
        });
        function stoppedByPage() {
            var ts = audioTracks();
            if (!ts.length || sourceEnded) return false;
            for (var i = 0; i < ts.length; i++) if (ts[i].readyState !== 'ended') return false;
            return true;
        }

        /* A pause is not a dead microphone. The window used to count ANY stretch
           of silence, so a person who stopped talking for 8–15 s was told their
           working microphone records silence. Two kinds of live input read as
           digital silence between phrases: those with a noise gate in the driver
           or headset (EXACT zeros), and very clean inputs with the browser's gain
           control off (measured on this reading: white noise below −65 dBFS RMS
           gives an all-zero spectrum; pink, closer to a real room, only between
           −80 and −85).
           Production, 30 days to 2026-09-24: 16 % of the watches that warned on
           /voice-changer warned two or more times in one microphone start — the
           input carried sound between the warnings — and 45 % on /screen-share;
           /dictaphone learned the same in its first two hours with a 3-s rule
           (seven audible sessions told «no sound» in their pauses).
           So the window counts only while the input has NEVER carried sound in
           this watch (two checks in a row above the floor — one stray bin is not
           life). Once it has spoken, only facts warn: the source ended the track,
           or the system muted it. Time is measured, not ticks: a background tab
           runs this at 1 Hz, and counting ticks stretched the window twofold. */
        var SUSTAIN = 2;
        var everAlive = false;
        var aboveRun = 0;
        var startedAt = Date.now();
        var lastTick = startedAt;
        var warnedAt = 0;
        function trackMuted() {
            var ts = audioTracks();
            return !!(ts[0] && ts[0].muted);
        }
        function slugOf() {
            return opts.slug || (document.querySelector('meta[name="tool-slug"]') || {}).content || '';
        }

        var freq = new Uint8Array(analyser.frequencyBinCount);
        function tick() {
            if (stopped) return;
            if (stoppedByPage()) {
                stopped = true;
                if (warned) { warned = false; try { opts.onSignal && opts.onSignal(); } catch (_) {} }
                if (ctx) { try { ctx.close(); } catch (_) {} }
                return;
            }
            if (!clockRunning()) { lastTick = Date.now(); setTimeout(tick, checkMs); return; }
            var nowT = Date.now();
            var step = Math.min(Math.max(nowT - lastTick, 0), Math.max(checkMs, 1500));
            lastTick = nowT;
            /* Decide on FREQUENCY data, not raw time-domain samples.
               `getByteTimeDomainData` hands back the most recent fftSize
               samples — 43 ms at 48 kHz — so a check every 500 ms was judging
               the microphone through an 8 % keyhole. Anything intermittent
               (a test tone, or a person who pauses between words) drops into
               the gaps, and sixteen unlucky keyholes in a row became "your
               microphone is recording silence". Measured on /voice-changer
               with a working mic: the watchdog fired in 4 of 6 runs while its
               own maxPeak had already reached 127 and the level meter sat at
               38-46 %.
               Frequency data comes off the same analyser but through its
               smoothing constant, which is a leaky integrator — exactly the
               "has there been ANY sound lately" question this asks. Digital
               silence still reads all-zero bins, so a genuinely dead input is
               still caught. */
            var peak = 0;
            try {
                analyser.getByteFrequencyData(freq);
                for (var i = 0; i < freq.length; i++) if (freq[i] > peak) peak = freq[i];
            } catch (_) {}
            if (peak > maxPeak) maxPeak = peak;

            if (peak > 2) {
                aboveRun++;
                if (aboveRun >= SUSTAIN) {
                    everAlive = true;
                    silentMs = 0;
                    if (warned) {
                        warned = false;
                        /* How long the warning stood before sound came: seconds
                           mean «they had not spoken yet», minutes mean «they fixed
                           the input» — the field tells them apart only through this. */
                        try {
                            if (window.__ae) window.__ae('feature_use', {
                                slug: slugOf(), action: 'mic_silence_recovered',
                                dead_ms: nowT - warnedAt, via: 'mic-silence-watch',
                            });
                        } catch (_) {}
                        try { opts.onSignal && opts.onSignal(); } catch (_) {}
                    }
                }
            } else {
                aboveRun = 0;
                var muted = trackMuted();
                if (everAlive && !sourceEnded && !muted) {
                    silentMs = 0;                 // it has spoken: this is a pause
                } else {
                    silentMs += step;
                    if (!warned && silentMs >= silenceMs) {
                        warned = true;
                        warnedAt = nowT;
                        try {
                            var track = opts.stream && opts.stream.getAudioTracks && opts.stream.getAudioTracks()[0];
                            if (window.__ae) window.__ae('feature_use', {
                                slug: slugOf(),
                                action: 'mic_silence_warned',
                                track_state: track ? track.readyState : 'none',
                                src_ended: sourceEnded ? 1 : 0,
                                alive: everAlive ? 1 : 0,
                                muted: muted ? 1 : 0,
                                t_ms: nowT - startedAt,
                                via: 'mic-silence-watch',
                            });
                        } catch (_) {}
                        try {
                            opts.onSilence && opts.onSilence({
                                alive: everAlive,
                                cause: sourceEnded ? 'ended' : (muted ? 'muted' : 'silent'),
                            });
                        } catch (_) {}
                    }
                }
            }
            setTimeout(tick, checkMs);
        }
        setTimeout(tick, checkMs);

        return {
            stop: function () {
                stopped = true;
                if (ctx) { try { ctx.close(); } catch (_) {} }
            },
            /** Highest byte-frequency bin seen over the whole watch (0-255).
             *  <=2 means the take never carried any energy at all. */
            maxPeak: function () { return maxPeak; },
        };
    }

    window.MicSilenceWatch = { attach: attach };
})();
